import type { Theme } from '@mui/material'
import { Box, LinearProgress, linearProgressClasses, styled, Tooltip, Typography } from '@mui/material'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentDisplayTooltip from 'components/ComponentDisplayTooltip'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React from 'react'

dayjs.extend(utc)
import type { MetricReductionResult, Target } from 'types/components'
import { ComponentSourceType, ComponentType, CompositeFunction, CompositeType } from 'types/components'
import { ThemeMode } from 'types/config'

interface TargetRowProps {
  target: Target
  findMetricValue: (targetComponentId: string, targetPeriodId: string, targetValue?: string) => MetricReductionResult
  calculateProgress: (targetComponentId: string, targetValue: string, targetPeriodId: string) => number
  theme: Theme
  componentType: string
  compositeType?: CompositeType
  compositeFunction?: CompositeFunction
  lowerIsBetter?: boolean
  hideLabel?: boolean
  targetLabel?: string
  periodLabel?: string
  currency?: string
  currencyDecimals?: number
  numericDecimalPlaces?: number
  precision?: number
  effectiveDate?: string
}

const TargetRow: React.FC<TargetRowProps> = ({
  target,
  findMetricValue,
  calculateProgress,
  theme,
  componentType,
  compositeType = CompositeType.COMPOSITE_TYPE_SIMPLE,
  compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE,
  lowerIsBetter = false,
  hideLabel = false,
  targetLabel,
  periodLabel,
  currency = 'USD',
  currencyDecimals = 0,
  numericDecimalPlaces,
  precision,
  effectiveDate,
}) => {
  const rawProgress = calculateProgress(target.componentId || '', target.value || '', target.periodId || '')
  const progress = isNaN(rawProgress) ? 0 : rawProgress
  const metricResult = findMetricValue(target.componentId || '', target.periodId || '', target.value || '')
  // const metricValue = metricResult.reducedValue

  const baseBarColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.info : theme.palette.primary
  const barColor = theme.palette.mode === ThemeMode.DARK ? 'primary' : 'primary'
  const barBackgroundColor = theme.palette.mode === ThemeMode.DARK ? baseBarColor.darker : baseBarColor.lighter
  const barBackgroundColor2 = theme.palette.mode === ThemeMode.DARK ? baseBarColor.light : baseBarColor.darker

  const OverTargetLinearProgress = styled(LinearProgress)({
    backgroundColor: barBackgroundColor,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: barBackgroundColor2,
    },
  })

  const DefaultLinearProgress = styled(LinearProgress)({
    backgroundColor: barBackgroundColor,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: barBackgroundColor,
    },
  })

  // Convert string componentType to enum
  const getComponentTypeEnum = (type: string): ComponentType => {
    switch (type) {
      case 'currency':
        return ComponentType.COMPONENT_TYPE_CURRENCY
      case 'percent':
        return ComponentType.COMPONENT_TYPE_PERCENTAGE
      case 'int':
      case 'integer':
        return ComponentType.COMPONENT_TYPE_INTEGER
      case 'float':
        return ComponentType.COMPONENT_TYPE_FLOAT
      case 'bool':
        return ComponentType.COMPONENT_TYPE_BOOL
      default:
        return ComponentType.COMPONENT_TYPE_FLOAT
    }
  }

  const componentTypeEnum = getComponentTypeEnum(componentType)
  const overTarget = progress > 0 ? (100 / progress) * 100 : 0

  const linearProgress =
    progress > 100 ? (
      <OverTargetLinearProgress
        color={barColor}
        variant="determinate"
        value={overTarget}
        sx={{ height: 12, borderRadius: 5 }}
      />
    ) : (
      <DefaultLinearProgress
        color={barColor}
        variant="determinate"
        value={progress}
        sx={{ height: 12, borderRadius: 5 }}
      />
    )

  return (
    <Box sx={{ mb: hideLabel ? 0 : 2 }}>
      {!hideLabel && <Typography variant="body1">{target.label}</Typography>}
      {targetLabel && (
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, display: 'flex', justifyContent: 'space-between' }}
        >
          {targetLabel}
        </Typography>
      )}
      {metricResult.functionUsed === 'none' ? (
        linearProgress
      ) : (
        <Tooltip
          title={
            <ComponentDisplayTooltip
              componentType={componentTypeEnum}
              value={metricResult.rawValue}
              sourceType={ComponentSourceType.COMPONENT_SOURCE_COMPOSITE}
              compositeFunction={metricResult.functionUsed as CompositeFunction}
              compositeType={compositeType}
              lowerIsBetter={lowerIsBetter}
              currency={currency}
              currencyDecimals={currencyDecimals}
              numericDecimalPlaces={numericDecimalPlaces}
              decimalPlaces={precision}
            />
          }
          arrow
          enterDelay={200}
          placement="top"
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                maxWidth: 'none',
                padding: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
              },
            },
          }}
        >
          {linearProgress}
        </Tooltip>
      )}

      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <span>
          Actual:{' '}
          <ComponentDisplay
            componentType={componentTypeEnum}
            value={metricResult.rawValue}
            sourceType={
              metricResult.functionUsed !== 'none'
                ? ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
                : ComponentSourceType.COMPONENT_SOURCE_MANUAL
            }
            compositeFunction={metricResult.functionUsed as CompositeFunction}
            compositeType={compositeType}
            lowerIsBetter={lowerIsBetter}
            componentLabel={target.label}
            periodLabel={periodLabel}
            fontSize="0.75rem"
            currency={currency}
            currencyDecimals={currencyDecimals}
            numericDecimalPlaces={numericDecimalPlaces}
            decimalPlaces={precision}
          />{' '}
          / Target:{' '}
          <ComponentDisplay
            componentType={componentTypeEnum}
            value={target.value || '0'}
            sourceType={(() => {
              // Detect if this is a composite target structure
              try {
                const parsedTarget = JSON.parse(target.value || '0')
                if (parsedTarget.value !== undefined && parsedTarget.compositeTargetValues !== undefined) {
                  return ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
                }
                return ComponentSourceType.COMPONENT_SOURCE_MANUAL
              } catch {
                return ComponentSourceType.COMPONENT_SOURCE_MANUAL
              }
            })()}
            compositeFunction={compositeFunction}
            compositeType={compositeType}
            componentLabel={target.label}
            periodLabel={periodLabel}
            isTarget={true}
            fontSize="0.75rem"
            currency={currency}
            currencyDecimals={currencyDecimals}
            numericDecimalPlaces={numericDecimalPlaces}
            decimalPlaces={precision}
          />
        </span>
        <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          {`${Math.round(progress)}%`}
        </Box>
      </Typography>
      {effectiveDate && (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Effective: {dayjs.utc(effectiveDate).format('MM/DD/YYYY')}
        </Typography>
      )}
    </Box>
  )
}

export default TargetRow
